import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bookmark, Sparkles, CheckCircle2, BookOpen, ExternalLink, FileText, Globe, ChevronDown, Play, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Math from "@/components/Math";

function Blueprint({ children }) {
  return (
    <div className="relative rounded-md border border-white/10 bg-[#0b1428] p-4 overflow-hidden">
      <div className="bp-grid absolute inset-0 opacity-70" />
      <div className="relative font-mono text-sm">{children}</div>
    </div>
  );
}

function TheorySection({ title, body, idx, open, onToggle }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#152040] overflow-hidden" data-testid={`theory-section-${idx}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid={`theory-toggle-${idx}`}
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-yellow-300 text-xs">{String(idx + 1).padStart(2, "0")}</span>
          <span className="font-display text-base font-semibold text-zinc-100"><Math>{title}</Math></span>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 fade-up">
          <div className="text-sm text-zinc-300 leading-relaxed border-l-2 border-cyan-500/40 pl-4">
            <Math>{body}</Math>
          </div>
        </div>
      )}
    </div>
  );
}

function VideoEmbed({ videoId, searchUrl, chapterTitle }) {
  const [loaded, setLoaded] = React.useState(false);
  if (!videoId && !searchUrl) return null;
  return (
    <div className="mb-6 rounded-xl border border-pink-300/25 bg-gradient-to-br from-pink-300/[0.06] via-transparent to-orange-300/[0.06] overflow-hidden" data-testid="video-embed-card">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-orange-300" />
          <div className="font-display text-sm uppercase tracking-[0.15em] text-orange-300 font-semibold">
            Watch this chapter
          </div>
        </div>
        {searchUrl && videoId && (
          <a href={searchUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-yellow-300 inline-flex items-center gap-1">
            More on YouTube <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {videoId ? (
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          {!loaded && (
            <button
              onClick={() => setLoaded(true)}
              className="absolute inset-0 grid place-items-center bg-black/40 hover:bg-black/30 transition-colors group"
              style={{ backgroundImage: `url(https://i.ytimg.com/vi/${videoId}/hqdefault.jpg)`, backgroundSize: "cover", backgroundPosition: "center" }}
              data-testid="video-play-btn"
              aria-label={`Play tutorial for ${chapterTitle}`}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 text-slate-900 fill-slate-900 ml-1" />
              </div>
            </button>
          )}
          {loaded && (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1`}
              title={`${chapterTitle} tutorial`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="video-iframe"
            />
          )}
        </div>
      ) : (
        <a
          href={searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group block px-5 pb-5"
          data-testid="video-search-fallback"
        >
          <div className="rounded-lg border border-white/10 bg-[#0b1428] p-4 flex items-center gap-4 hover:border-orange-300/40 transition-colors">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-300 via-orange-300 to-yellow-300 grid place-items-center shadow-lg group-hover:scale-105 transition-transform shrink-0">
              <Play className="w-6 h-6 text-slate-900 fill-slate-900 ml-1" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-semibold text-zinc-100 group-hover:text-orange-200 transition-colors">
                Top CBSE tutorials for {chapterTitle}
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                Curated video coming soon. For now, open the freshest CBSE-focused YouTube results — Magnet Brains, LearnoHub, Physics Wallah &amp; more.
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-orange-300 shrink-0" />
          </div>
        </a>
      )}
    </div>
  );
}

function ResourceLinks({ resources, chapterTitle }) {
  if (!resources) return null;
  const links = [
    { key: "ncert_pdf", label: "Official NCERT PDF", icon: FileText, hint: "Latest 2026-27 edition on ncert.nic.in" },
    { key: "ncert_book", label: "NCERT Book Portal", icon: BookOpen, hint: "Browse every chapter of the latest book" },
    { key: "cbse_portal", label: "CBSE Academic Portal", icon: Globe, hint: "Official CBSE curriculum" },
  ].filter((l) => resources[l.key]);

  if (!links.length) return null;

  return (
    <div className="rounded-xl border border-pink-300/25 bg-gradient-to-br from-pink-300/[0.06] to-transparent p-5 mb-8" data-testid="resource-links">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-yellow-300" />
          <div className="font-display text-sm uppercase tracking-[0.15em] text-yellow-300 font-semibold">
            Read the full chapter
          </div>
        </div>
        {resources.book_title && (
          <div
            className="text-[10px] uppercase tracking-[0.15em] text-pink-300 border border-pink-300/30 rounded-full px-2.5 py-1"
            data-testid="resource-book-title"
          >
            {resources.book_title}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <a
              key={l.key}
              href={resources[l.key]}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-3 rounded-lg border border-white/10 bg-[#0f1a34] p-3 hover:border-orange-300/50 hover:bg-[#152040] transition-colors"
              data-testid={`resource-${l.key}`}
            >
              <Icon className="w-4 h-4 text-yellow-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-100 group-hover:text-yellow-100">
                  {l.label}
                  <ExternalLink className="w-3 h-3 text-zinc-500 group-hover:text-yellow-300 shrink-0" />
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 truncate">{l.hint}</div>
              </div>
            </a>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-zinc-500">
        Tip: NCERT updated the syllabus for 2026-27. The links above open the latest edition on the official portal — chapter titles and numbering may differ from the notes shown here.
      </div>
    </div>
  );
}

export default function ChapterDetail() {
  const { chapterId } = useParams();
  const [chapter, setChapter] = useState(null);
  const [openIdx, setOpenIdx] = useState({});
  const [openTheory, setOpenTheory] = useState({ 0: true });
  const { user } = useAuth();
  const [hasNotes, setHasNotes] = useState(false);

  useEffect(() => {
    api.get(`/chapters/${chapterId}`).then((r) => setChapter(r.data));
    api.get("/notes").then((r) => setHasNotes(r.data.some((n) => n.chapter_id === chapterId))).catch(() => setHasNotes(false));
  }, [chapterId]);

  const bookmark = async (q) => {
    if (!user) return toast.error("Log in to bookmark");
    try {
      await api.post("/bookmarks", {
        class_level: chapter.class_id,
        chapter_id: chapter.id,
        question_id: q.id,
        question_text: q.q,
      });
      toast.success("Bookmarked");
    } catch { toast.error("Could not bookmark"); }
  };

  if (!chapter) return <div className="text-zinc-500">Loading chapter…</div>;

  const hasDeep = Array.isArray(chapter.deep_theory) && chapter.deep_theory.length > 0;

  return (
    <div className="max-w-4xl mx-auto fade-up">
      <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">
        {chapter.class_label} · Chapter {chapter.number}
      </div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-3" data-testid="chapter-title">{chapter.title}</h1>
      <p className="text-zinc-400 leading-relaxed mb-6"><Math>{chapter.summary}</Math></p>

      <div className="flex flex-wrap gap-2 mb-6">
        {hasNotes && (
          <Link to={`/chapters/${chapter.id}/notes`}>
            <Button className="bg-yellow-300 hover:bg-yellow-200 text-slate-900 rounded-full font-semibold" data-testid="cta-read-notes">
              <BookOpen className="w-4 h-4 mr-1" /> Read chapter notes
            </Button>
          </Link>
        )}
        <Link to={`/practice/${chapter.id}`}>
          <Button className="bg-sky-400 hover:bg-sky-300 text-slate-900 rounded-full font-semibold" data-testid="cta-practice">
            <Play className="w-4 h-4 mr-1" /> Take practice test
          </Button>
        </Link>
        <Link to={`/worksheet/${chapter.id}`}>
          <Button variant="outline" className="rounded-full border-white/15 bg-transparent text-zinc-200 hover:bg-white/5" data-testid="cta-worksheet">
            <Printer className="w-4 h-4 mr-1" /> Printable worksheet
          </Button>
        </Link>
      </div>

      <VideoEmbed
        videoId={chapter.resources?.video_embed_id}
        searchUrl={chapter.resources?.video}
        chapterTitle={chapter.title}
      />

      <ResourceLinks resources={chapter.resources} chapterTitle={chapter.title} />

      <Tabs defaultValue="theory" className="w-full">
        <TabsList className="bg-[#152040] border border-white/10">
          <TabsTrigger value="theory" data-testid="tab-theory">Theory</TabsTrigger>
          <TabsTrigger value="examples" data-testid="tab-examples">Worked examples</TabsTrigger>
          <TabsTrigger value="exercise" data-testid="tab-exercise">Exercise</TabsTrigger>
          <TabsTrigger value="extra" data-testid="tab-extra">Extra practice</TabsTrigger>
        </TabsList>

        <TabsContent value="theory" className="mt-6 space-y-6">
          {hasDeep && (
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                Detailed theory
              </div>
              {chapter.deep_theory.map((t, i) => (
                <TheorySection
                  key={i}
                  idx={i}
                  title={t.title}
                  body={t.body}
                  open={!!openTheory[i]}
                  onToggle={() => setOpenTheory((s) => ({ ...s, [i]: !s[i] }))}
                />
              ))}
            </div>
          )}

          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Key concepts &amp; formulas
            </div>
            {chapter.key_concepts.map((k, i) => (
              <div key={i} className="rounded-md border border-white/10 bg-[#152040] p-4 flex gap-3">
                <div className="font-mono text-yellow-300 text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-sm text-zinc-200 leading-relaxed"><Math>{k}</Math></div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="examples" className="mt-6 space-y-6">
          {chapter.worked_examples.map((ex, i) => (
            <div key={i} className="rounded-lg border border-white/10 bg-[#152040] p-5">
              <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-2">Example {i + 1}</div>
              <div className="font-display text-lg font-semibold mb-4"><Math>{ex.q}</Math></div>
              <Blueprint>
                {ex.steps.map((s, j) => (
                  <div key={j} className="flex gap-3 mb-1.5">
                    <span className="text-yellow-300">{String(j + 1).padStart(2, "0")}</span>
                    <span className="text-zinc-200 bp-math"><Math>{s}</Math></span>
                  </div>
                ))}
                <div className="flex gap-3 mt-3 pt-3 border-t border-white/10">
                  <CheckCircle2 className="w-4 h-4 text-pink-300 mt-0.5" />
                  <span className="text-pink-300"><Math>{ex.answer}</Math></span>
                </div>
              </Blueprint>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="exercise" className="mt-6 space-y-4">
          {chapter.exercise.map((q, i) => {
            const open = !!openIdx[i];
            return (
              <div key={q.id} className="rounded-lg border border-white/10 bg-[#152040] p-5" data-testid={`exercise-${q.id}`}>
                <div className="flex justify-between gap-4 items-start">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Q{i + 1}</div>
                    <div className="font-display text-base font-semibold leading-snug"><Math>{q.q}</Math></div>
                  </div>
                  <button onClick={() => bookmark(q)} className="text-zinc-500 hover:text-yellow-300" data-testid={`bookmark-${q.id}`}>
                    <Bookmark className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/15 bg-transparent text-yellow-300 hover:bg-orange-300/10 rounded-full"
                    onClick={() => setOpenIdx((s) => ({ ...s, [i]: !s[i] }))}
                    data-testid={`toggle-solution-${q.id}`}
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> {open ? "Hide" : "Show"} solution
                  </Button>
                </div>
                {open && (
                  <div className="mt-4 fade-up">
                    <Blueprint>
                      {q.solution.map((s, j) => (
                        <div key={j} className="flex gap-3 mb-1.5">
                          <span className="text-yellow-300">{String(j + 1).padStart(2, "0")}</span>
                          <span className="text-zinc-200 bp-math"><Math>{s}</Math></span>
                        </div>
                      ))}
                      <div className="flex gap-3 mt-3 pt-3 border-t border-white/10">
                        <CheckCircle2 className="w-4 h-4 text-pink-300 mt-0.5" />
                        <span className="text-pink-300"><Math>{q.final}</Math></span>
                      </div>
                    </Blueprint>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="extra" className="mt-6 space-y-3">
          {chapter.extra_practice.map((p, i) => {
            const isObj = p && typeof p === "object";
            const qText = isObj ? p.q : p;
            const finalAns = isObj ? p.final : null;
            const solutionSteps = isObj && Array.isArray(p.solution) ? p.solution : null;
            return (
              <div key={i} className="rounded-md border border-dashed border-white/10 bg-[#0f1a34] p-4 flex gap-3" data-testid={`extra-item-${i}`}>
                <span className="font-mono text-yellow-300 text-xs mt-0.5">+{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200"><Math>{qText}</Math></div>
                  {(solutionSteps || finalAns) && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-zinc-500 hover:text-yellow-300">Show hint</summary>
                      {solutionSteps && (
                        <ol className="mt-1.5 pl-4 list-decimal text-zinc-400 space-y-0.5">
                          {solutionSteps.map((s, si) => (
                            <li key={si}><Math>{s}</Math></li>
                          ))}
                        </ol>
                      )}
                      {finalAns && (
                        <div className="mt-1.5 text-emerald-300">
                          <span className="text-zinc-500">Answer: </span>
                          <Math>{finalAns}</Math>
                        </div>
                      )}
                    </details>
                  )}
                </div>
              </div>
            );
          })}
          <div className="mt-4 text-xs text-zinc-500">Try these — then paste any tricky one into the AI Tutor for a full walkthrough.</div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
